import { useCallback, useEffect, useMemo, lazy, Suspense } from 'react';
import Order from './infoOrderOrder';
import Patient from './infoOrderPatient';
import './infoOrder.scss';
import { useAppDispatch, useAppSelector } from '../../../contexts/hooks';
import { selectOrdersPermissions, selectColumnsContainerOrderOrders, selectColumnsContainerPatientOrders, selectModuleTracking, selectModuleClinicalData, selectModuleSamples, selectModuleAttachment, selectModuleInformedConsents } from '../../../contexts/selectors';
import TabPanel from 'devextreme-react/tab-panel';
import { formatMessage } from 'devextreme/localization';
import { useApiOrders } from '../../../api/api.orders';
import { setCurrentActiveTab } from '../../../contexts/slices/orders';
import { LoadPanel } from 'devextreme-react';

// Lazy loading de componentes para optimizar la carga inicial
// Solo se cargan cuando el usuario accede a cada tab
const Studies = lazy(() => import('./OrderModules/OrdStudies'));
const Results = lazy(() => import('./OrderModules/OrdResults'));
const Notes = lazy(() => import('./OrderModules/OrdNotes'));
const Attachments = lazy(() => import('./OrderModules/OrdAttachments'));
const Tracking = lazy(() => import('./OrderModules/OrdTracking'));
const SerumLibrary = lazy(() => import('./OrderModules/OrdSerumLibrary'));
const OrdInformedConsents = lazy(() => import('./OrderModules/OrdInformedConsents'));

const Index = () => {
  const dispatch = useAppDispatch();
  const { getLstBacteriologist } = useApiOrders();

  // Selectores consolidados optimizados
  const permissions = useAppSelector(selectOrdersPermissions);
  const ColumnsContainerOrderOrders = useAppSelector(selectColumnsContainerOrderOrders);
  const ColumnsContainerPatientOrders = useAppSelector(selectColumnsContainerPatientOrders);
  const ModuleTracking = useAppSelector(selectModuleTracking);
  const ModuleClinicalData = useAppSelector(selectModuleClinicalData);
  const ModuleSamples = useAppSelector(selectModuleSamples);
  const ModuleAttachment = useAppSelector(selectModuleAttachment);
  const ModuleInformedConsents = useAppSelector(selectModuleInformedConsents);

  // Otros selectores
  const Prm_Res = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res);
  const notes = useAppSelector((x) => x.globalOrders.CurrentOrder.Notes);
  const attachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const consentsCount = useAppSelector((x) => x.globalOrders.CurrentOrder.ConsentsCount);
  const lstAnalitosResultTipoListas = useAppSelector((x) => x.configuration.LstAnalitosResulTipoListas);

  const tabs = useMemo(
    () =>
      [
        { id: 1, title: 'OrdStudies', visible: permissions.canViewOrders },
        { id: 2, title: 'OrdResults', visible: Prm_Res },
        { id: 3, title: 'OrdNotes', visible: permissions.canReadNotes },
        { id: 4, title: 'OrdAttachments', visible: permissions.canViewAttachments && ModuleAttachment },
        { id: 5, title: 'OrdTracking', visible: ModuleTracking && permissions.canViewTracking },
        { id: 6, title: 'OrdSerumLibrary', visible: permissions.canViewSerumLibrary && ModuleSamples },
        { id: 7, title: 'OrdClinicalData', visible: ModuleClinicalData && permissions.canViewClinicalData },
        { id: 8, title: 'OrdInformedConsents', visible: ModuleInformedConsents && permissions.canViewInformedConsents },
      ].filter((x) => x.visible),
    [permissions, Prm_Res, ModuleTracking, ModuleClinicalData, ModuleSamples, ModuleAttachment, ModuleInformedConsents]
  );

  // Leer el tab ID actual desde Redux
  const currentActiveTabId = useAppSelector((x) => x.globalOrders.currentActiveTab) ?? 1;

  // Calcular el índice directamente con useMemo - SIN useState ni useEffect
  // Esto evita re-renders innecesarios y múltiples ejecuciones
  const selectedIndex = useMemo(() => {
    // alert(`Tab calculado: ${currentActiveTabId}`);
    const index = tabs.findIndex((tab) => tab.id === currentActiveTabId);
    return index >= 0 ? index : (Prm_Res ? 1 : 0);
  }, [tabs, currentActiveTabId, Prm_Res]);

  // Componente de loading mientras se cargan los módulos lazy
  const LoadingFallback = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <LoadPanel visible={true} showPane={false} />
      </div>
    ),
    []
  );

  // Render function optimizada que evita recrear elementos JSX en cada render
  // Ahora usa lazy loading real con Suspense
  const renderItemP = useCallback(
    (props: any) => {
      const tabId = props.data.id;

      // Renderizar el componente correspondiente envuelto en Suspense
      switch (tabId) {
        case 1:
          return (
            <Suspense fallback={LoadingFallback}>
              <Studies />
            </Suspense>
          );
        case 2:
          return (
            <Suspense fallback={LoadingFallback}>
              <Results />
            </Suspense>
          );
        case 3:
          return (
            <Suspense fallback={LoadingFallback}>
              <Notes />
            </Suspense>
          );
        case 4:
          return (
            <Suspense fallback={LoadingFallback}>
              <Attachments />
            </Suspense>
          );
        case 5:
          return (
            <Suspense fallback={LoadingFallback}>
              <Tracking />
            </Suspense>
          );
        case 6:
          return (
            <Suspense fallback={LoadingFallback}>
              <SerumLibrary />
            </Suspense>
          );
        case 8:
          return (
            <Suspense fallback={LoadingFallback}>
              <OrdInformedConsents />
            </Suspense>
          );
        default:
          return null;
      }
    },
    [LoadingFallback]
  );

  // Manejar cambio de tab - actualizar Redux (que a su vez actualizará selectedIndex via useMemo)
  const onSelectionChanged = useCallback((args: any) => {
    if (args.name === 'selectedIndex') {
      const newIndex = args.value;
      const tabId = tabs[newIndex]?.id;
      if (tabId) {
        dispatch(setCurrentActiveTab(tabId));
      }
    }
  }, [tabs, dispatch]);

  // Efecto de inicialización - solo se ejecuta UNA vez al montar
  useEffect(() => {
    if (lstAnalitosResultTipoListas.length === 0 && Prm_Res) {
      getLstBacteriologist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemTitleRender = (item) => {
    let counter = 0;
    let showBadge = false;
    switch (item.id) {
      case 1:
      case 2:
      case 5:
      case 6:
      default:
        break;
      case 3:
        counter = notes?.length ?? 0;
        showBadge = counter > 0;
        break;
      case 4:
        counter = attachments?.length ?? 0;
        showBadge = counter > 0;
        break;
      case 8:
        counter = consentsCount ?? 0;
        showBadge = counter > 0;
        break;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>{formatMessage(item.title)}</span>
        {showBadge && <span className='badge-tab'>{counter}</span>}
      </div>
    );
  };

  return (
    <div>
      <div className='container-forms-ord'>
        <div className={`colOrd-${ColumnsContainerOrderOrders || 6}`}>
          <Order />
        </div>
        <div className={`colOrd-${ColumnsContainerPatientOrders || 6}`}>
          <Patient />
        </div>
      </div>
      <div className='dx-card details-card tab-panel-orders'>
        <TabPanel dataSource={tabs} deferRendering={true} activeStateEnabled={true} swipeEnabled={false} selectedIndex={selectedIndex} itemTitleRender={itemTitleRender} onOptionChanged={onSelectionChanged} animationEnabled={false} loop={false} itemComponent={renderItemP} />
      </div>
    </div>
  );
};

export default Index;
